import styled from '@emotion/styled';
import type { RefObject } from 'react';

import { Stack } from '@/components/ui/Stack';
import { Heading, Text } from '@/components/ui/Text';
import { headingFont, mqMax } from '@/lib/theme';
import { useI18n } from '@/locales';
import type { IProperty } from '@/types/content';

import { RatingBadge } from './reviews/RatingBadge';

/* Centred at the top of the panel on phones, like the check-in's welcome. */
const Header = styled(Stack)`
	${mqMax.md} {
		align-items: center;
		text-align: center;
	}
`;

/* A step up on phones, kept narrow and balanced so it reads as a title, not a line. */
const Title = styled(Heading)`
	${mqMax.md} {
		${headingFont('RWB140')}
		max-width: 12em;
		text-wrap: balance;
	}
`;

const scrollTo = (ref?: RefObject<HTMLElement | null>) =>
	ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

interface PropertyTitleProps {
	property: IProperty;
	rating?: { average: number; count: number } | null;
	reviewsRef?: RefObject<HTMLElement | null>;
}

/** "lodge" / "bed_and_breakfast" -> "Lodge" / "Bed and breakfast". */
const formatPropertyType = (type: string) => {
	const text = type.replace(/_/g, ' ').trim();
	return text.charAt(0).toUpperCase() + text.slice(1);
};

const plural = (
	t: ReturnType<typeof useI18n>,
	count: number,
	one: 'guest' | 'bedroom' | 'bed' | 'bathroom',
	many: 'guests' | 'bedrooms' | 'beds' | 'bathrooms',
) =>
	count === 1
		? t(`property.capacity.${one}`)
		: t(`property.capacity.${many}`, { count });

/** Name, rating, location and capacity: the first thing a guest reads. */
export const PropertyTitle = ({
	property,
	rating,
	reviewsRef,
}: PropertyTitleProps) => {
	const t = useI18n();
	const bedrooms = property.bedrooms.filter(
		(b) => b.room_type !== 'common_spaces',
	).length;

	return (
		<Header gap="space40">
			<Header gap="space20">
				<Title as="h1" size="xl">
					{property.name}
				</Title>
				<div>
					<Text variant="bodySmall">
						{property.property_type
							? t('property.location.typeIn', {
									type: formatPropertyType(
										property.property_type,
									),
									place: `${property.city}, ${property.country}`,
								})
							: `${property.city}, ${property.country}`}
					</Text>
					<Text variant="bodySmall">
						{[
							plural(t, property.capacity, 'guest', 'guests'),
							plural(t, bedrooms, 'bedroom', 'bedrooms'),
							plural(t, property.beds.length, 'bed', 'beds'),
							plural(
								t,
								property.bathrooms.length,
								'bathroom',
								'bathrooms',
							),
						].join(' · ')}
					</Text>
				</div>
			</Header>
			{rating && rating.count > 0 && (
				<RatingBadge
					average={rating.average}
					count={rating.count}
					onClick={() => scrollTo(reviewsRef)}
				/>
			)}
		</Header>
	);
};
